import { useEffect, useRef, useState, type RefObject } from 'react';

export function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Becomes true once the element has entered the viewport (one-shot). */
export function useInView<T extends Element>(options: { threshold?: number; rootMargin?: string } = {}): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  const { threshold = 0.25, rootMargin = '0px' } = options;
  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return;
    if (!('IntersectionObserver' in window)) { setInView(true); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setInView(true); observer.disconnect(); }
    }, { threshold, rootMargin });
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, threshold, rootMargin]);
  return [ref, inView];
}

type ProgressListener = () => void;
const listeners = new Set<ProgressListener>();
let ticking = false;
function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => { ticking = false; listeners.forEach(listener => listener()); });
}

/**
 * Writes the element's scroll progress (0 → 1 while it travels through the viewport) to a CSS
 * custom property on the element itself, so scroll-linked visuals never re-render React.
 * `mode: 'sticky'` measures progress across a tall section whose content is position:sticky.
 */
export function useScrollProgress<T extends HTMLElement>(ref: RefObject<T | null>, options: { mode?: 'through' | 'sticky'; property?: string; onProgress?: (value: number) => void } = {}) {
  const { mode = 'through', property = '--p' } = options;
  const callback = useRef(options.onProgress);
  callback.current = options.onProgress;
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (prefersReducedMotion()) { node.style.setProperty(property, '1'); callback.current?.(1); return; }
    const update = () => {
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight;
      const raw = mode === 'sticky' ? -rect.top / Math.max(1, rect.height - vh) : (vh - rect.top) / (vh + rect.height);
      const value = Math.min(1, Math.max(0, raw));
      node.style.setProperty(property, value.toFixed(4));
      callback.current?.(value);
    };
    update();
    listeners.add(update);
    if (listeners.size === 1) { window.addEventListener('scroll', onScroll, { passive: true }); window.addEventListener('resize', onScroll); }
    return () => {
      listeners.delete(update);
      if (!listeners.size) { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); }
    };
  }, [ref, mode, property]);
}

/** Matches a media query after hydration (server render assumes `false`). */
export function useMedia(query: string) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);
  return matches;
}
